import * as React from 'react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  MenstrualPhase,
  PanelField,
  PanelMarker,
} from '../../models';
import { Card, Table, OverlayTrigger, Tooltip, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { MathUtils } from '../../utils/MathUtils';
import Moment from 'react-moment';
import { UserProfile } from '../../models/UserProfile';
import Storage from '@aws-amplify/storage';
import { PDFModal } from '../common/pdf-modal';
import { GenericModal } from '../common/generic-modal';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');
const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');
const logo = require('../../assets/imgs/logo.png');

const NUM_RESULTS_TO_COMPARE: number = 10;
const NUM_RESULTS_TO_COMPARE_FOR_PDF: number = 5;

interface Props {
  bloodworks: Bloodwork[];
  bloodworkRanges: BloodworkRange[];
  currentUserProfile: UserProfile;
  onExportToPdf: Function;
  onHidePdfModal: Function;
}

type State = {
  showPdfModal: boolean;
  showUpgradeModal: boolean;
  numResultsToCompare: number;
  headerLogoSrc?: string; // resolved preview URL for the modal's <Image id="logo">
  showHiddenRows: boolean; // toggle to show/hide rows with no value
};

export class BloodworkComparisonAnalysis extends React.Component<Props, State> {
  state: State = {
    showPdfModal: false,
    showUpgradeModal: false,
    numResultsToCompare: NUM_RESULTS_TO_COMPARE,
    headerLogoSrc: undefined,
    showHiddenRows: false, // default: hide empty rows
  };

  // ----------- Helper to Hide Rows with No Values ---------
  /** Returns true if this marker has a value in at least one result in the history.
   *  Special-case menstrualPhase to consider altValue as a "value".
   */
  private fieldHasAnyValue(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    return history.some(bwa => {
      const pm = this.findPanelMarker(field, bwa);
      if (!pm) return false;

      if (field.id === 'menstrualPhase') {
        const alt = pm.altValue;

        // Treat undefined, null, or empty/whitespace string as "no value"
        // So menstrual phase behaves like other markers when hide blank rows is ON.
        return alt !== undefined && alt !== null && String(alt).trim() !== '';
      }

      return typeof pm.hasValue === 'function' && pm.hasValue();
    });
  }

  /** Returns true if any field in the section has a value in the entire history */
  private sectionHasAnyValues(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): boolean {
    return fields.some(f => this.fieldHasAnyValue(f, history));
  }

  /** Count how many fields would render given the current hide/show blanks setting.
   *  - When showHiddenRows === false (Hide blank rows is CHECKED):
   *      Only fields with at least one value in the history are counted.
   *  - When showHiddenRows === true (Hide blank rows is UNCHECKED):
   *      All non-hidden fields are counted, even if they have no values.
   */
  private countVisibleFields(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): number {
    const hideBlanks = !this.state.showHiddenRows;
    return fields.filter(f =>
      !f.isHiddenFromComparison &&
      (hideBlanks ? this.fieldHasAnyValue(f, history) : true),
    ).length;
  }

  /** Returns true if any of the iron markers in PanelField.IRON_FIELDS
   *  has a value anywhere in the history.
   *
   *  Used for the "hide blank rows" rule:
   *  - When hide blank rows is ON:
   *      If none of these iron markers has a value, the entire
   *      "Iron Related Markers" section is hidden (UI + PDF).
   *  - When hide blank rows is OFF:
   *      The section is always shown, even if all values are blank.
   */
  private anyIronCoreMarkerHasValue(
    history: BloodworkAnalysis[],
  ): boolean {
    const ironOnlyFields = [...PanelField.IRON_FIELDS];
    return this.sectionHasAnyValues(ironOnlyFields, history);
  }


  // ---------- Logo helpers (preview reliability) ----------

  getLogoUrl = (): string => {
    const { currentUserProfile } = this.props;

    return (
      currentUserProfile?.hasCustomLogo()
        ? currentUserProfile.customLogoUrl
        : (logo as any)
    ) as any;
  };

  /** Normalize whatever we store (URL, key, filename) into an S3 key if possible. */
  private extractUserLogoKey = (candidate: string): string | undefined => {
    if (!candidate) return undefined;

    // Already a key like "user-logos/abc.png"
    if (/^user-logos\//i.test(candidate)) {
      return candidate.replace(/^\/+/, '');
    }

    // "public/user-logos/..." (Amplify storage path)
    const publicIdx = candidate.indexOf('public/user-logos/');
    if (publicIdx >= 0) {
      return candidate.slice(publicIdx + 'public/'.length);
    }

    // Any URL that contains "/user-logos/..."
    const seg = '/user-logos/';
    const idx = candidate.indexOf(seg);
    if (idx >= 0) {
      return candidate.slice(idx + 1); // drop the leading '/'
    }

    // Bare filename? assume user-logos/<filename>
    const isUrl = /^https?:\/\//i.test(candidate) || /^data:/i.test(candidate);
    if (!isUrl) {
      const fn = candidate.split('/').pop();
      if (fn) return `user-logos/${fn}`;
    }

    return undefined;
  };

  /** Convert any URL to a data URL for reliable rendering */
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  /** Ensure the preview <Image id="logo"> gets a real, loadable data URL. */
  private ensureHeaderPreviewLogo = async () => {
    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // Fallback bundled asset (always resolvable)
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // If nothing set, convert bundled to data URL
    if (!candidate) {
      try {
        const dataUrl = await this.toDataUrl(bundled);
        this.setState({ headerLogoSrc: dataUrl });
      } catch {
        this.setState({ headerLogoSrc: bundled });
      }
      return;
    }

    // If full URL, convert to data URL
    if (/^https?:\/\//i.test(candidate)) {
      try {
        const dataUrl = await this.toDataUrl(candidate);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      } catch {
        // fall through to S3/bundled
      }
    }

    // If already a data URL, use it directly
    if (/^data:/i.test(candidate)) {
      this.setState({ headerLogoSrc: candidate });
      return;
    }

    // Otherwise try to sign via Storage.get and convert to data URL
    try {
      const key = this.extractUserLogoKey(candidate);
      if (key) {
        const signed = (await Storage.get(key)) as string;
        const dataUrl = await this.toDataUrl(signed);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      }
    } catch {
      // fall through
    }

    // Fallback to bundled (convert to data URL)
    try {
      const dataUrl = await this.toDataUrl(bundled);
      this.setState({ headerLogoSrc: dataUrl });
    } catch {
      this.setState({ headerLogoSrc: bundled });
    }
  };

  // -------------------------------------------------------

  componentDidMount() {
    // Make sure modal preview shows a loadable logo URL
    this.ensureHeaderPreviewLogo();
  }

  findPanelMarker(
    panelField: PanelField,
    bloodworkAnalysis: BloodworkAnalysis,
  ): PanelMarker {
    return bloodworkAnalysis.findPanelMarker(panelField.id);
  }

  displayValue(panelMarker: PanelMarker) {
    if (panelMarker) {
      return panelMarker.hasValue()
        ? MathUtils.round(panelMarker.value, panelMarker.field.precision)
        : null;
    } else {
      return 'N/A';
    }
  }

  getClassName(pm: PanelMarker): string {
    if (!pm || pm.isBlank() || pm.isOptimal()) {
      return '';
    }

    if (pm.field.noRedChart) {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (!pm.isLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.noRedOrangeChart) {
      if (pm.isNotOptimal() && pm.isLessThanOrIsLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.useRDWChart) {
      if (pm.isGreaterThanOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isNotLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (!pm.isAlarm()) {
        return 'bg-red';
      }
    }
    return '';
  }

  getIcon(pm: PanelMarker) {
    if (pm.isBlank()) return null;
    let img: any;
    if (pm.isGreaterThanOptimal()) {
      img = arrow_up;
    } else if (pm.isStricklyLessThanOptimal()) {
      img = arrow_down;
    }
    return <img src={img} alt="" />;
  }

  comparisonDetails(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    pm: PanelMarker,
    index: number,
  ) {
    if (!pm) {
      return null;
    }

    if (pm.field.id === 'menstrualPhase') {
      return (
        <td>
          {pm.altValue === MenstrualPhase.NONE ? (
            <>
              {MenstrualPhase.NONE}
              <br />
              (children&apos;s ranges)
            </>
          ) : (
            pm.altValue
          )}
        </td>
      );
    }

    let img, imgSrc;
    const valueDetail = (
      <td className={this.getClassName(pm)} key={index}>
        {this.displayValue(pm)}&nbsp;&nbsp;{this.getIcon(pm)}
      </td>
    );
    if (index + 1 < bloodworkAnalysisHistory.length) {
      return valueDetail;
    } else {
      if (
        pm &&
        pm.hasValue() &&
        pm.previousPanelMarker &&
        pm.previousPanelMarker.hasValue()
      ) {
        if (pm.isOptimal() && pm.previousPanelMarker.isOptimal()) {
          // No op
        } else {
          if (
            Math.abs(pm.percentFromMean) <
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_up;
          } else if (
            Math.abs(pm.percentFromMean) >
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_down;
          }
          img = <img src={imgSrc} alt="" />;
        }
      }

      return (
        <>
          {valueDetail}
          <td style={{ textAlign: 'center' }}>{img}</td>
          <td>{pm.range?.alarmMin}</td>
          <td>{pm.range?.labMin}</td>
          <td>{pm.range?.optimalMin}</td>
          <td>{pm.range?.optimalMax}</td>
          <td>{pm.range?.labMax}</td>
          <td>{pm.range?.alarmMax}</td>
        </>
      );
    }
  }

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  export() {
    this.props.onExportToPdf();
    if (this.props.currentUserProfile.pdfExportComparisonTableAllowed) {
      this.setState({
        showPdfModal: true,
        numResultsToCompare: NUM_RESULTS_TO_COMPARE_FOR_PDF,
      });
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  comparisonTable(
  bloodworkAnalysisHistory: BloodworkAnalysis[],
  panelFields: PanelField[],
  clientGender?: string, //For menstrual phase - not showing if gender is male
  ) {
  // Determine which rows to show
  const hideBlanks = !this.state.showHiddenRows; // checkbox checked = hide blanks
  
  const visibleFields = panelFields.filter((field: PanelField) => {
    if (field.isHiddenFromComparison) return false;
  
  // Never show menstrual phase for male clients
    if (field.id === 'menstrualPhase' && clientGender === 'M') {
      return false;
    }
  
  const hasAny = this.fieldHasAnyValue(field, bloodworkAnalysisHistory);
    // When hideBlanks is true: only fields with data (incl. menstrualPhase via altValue).
    // When hideBlanks is false: show all fields (incl. menstrualPhase) if not male.
    return hideBlanks ? hasAny : true; // default hide; show all when unchecked
  });

  // If nothing to show, don't render a table at all
  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <Table striped bordered hover className="labsmartz-table">
      <thead>
        <tr>
          <th>Marker</th>
          <th>Units</th>
          {bloodworkAnalysisHistory.map(
            (bloodworkAnalysis: BloodworkAnalysis, index: number) => {
              return (
                <th key={index}>
                  <Moment format="MM/DD/YYYY">
                    {bloodworkAnalysis.bloodwork.collectionDate}
                  </Moment>
                </th>
              );
            },
          )}
          <th>Trend</th>
          <th>Alarm Min</th>
          <th>Lab Min</th>
          <th>Optimal Min</th>
          <th>Optimal Max</th>
          <th>Lab Max</th>
          <th>Alarm Max</th>
        </tr>
      </thead>

      <tbody>
        {visibleFields.map((field: PanelField, rowIdx: number) => {
          const valueCells = bloodworkAnalysisHistory.map((bwa, colIdx) => {
            const pm: PanelMarker | undefined = this.findPanelMarker(field, bwa);

            if (pm && field.id === 'menstrualPhase') {
              return (
                <td key={`v-${rowIdx}-${colIdx}`}>
                  {pm.altValue === MenstrualPhase.NONE ? (
                    <>
                      {MenstrualPhase.NONE}
                      <br />(children&apos;s ranges)
                    </>
                  ) : (
                    pm.altValue
                  )}
                </td>
              );
            }

            return (
              <td
                key={`v-${rowIdx}-${colIdx}`}
                className={pm ? this.getClassName(pm) : ''}
              >
                {pm?.hasValue?.() ? this.displayValue(pm) : null}
                &nbsp;&nbsp;
                {pm ? this.getIcon(pm) : null}
              </td>
            );
          });

          const lastPm = this.findPanelMarker(
            field,
            bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 1],
          );

          let trend: React.ReactNode = null;
          if (
            lastPm?.hasValue?.() &&
            lastPm.previousPanelMarker?.hasValue?.() &&
            !(lastPm.isOptimal() && lastPm.previousPanelMarker.isOptimal())
          ) {
            const improved =
              Math.abs(lastPm.percentFromMean) <
              Math.abs(lastPm.previousPanelMarker.percentFromMean);
            const worsened =
              Math.abs(lastPm.percentFromMean) >
              Math.abs(lastPm.previousPanelMarker.percentFromMean);
            trend = improved ? <img src={thumbs_up} alt="" /> : worsened ? <img src={thumbs_down} alt="" /> : null;
          }

          return (
            <tr key={`r-${rowIdx}`}>
              <td>{field.getChartLabel()}</td>
              <td>{field.getChartUnits()}</td>
              {valueCells}
              <td style={{ textAlign: 'center' }}>{trend}</td>
              <td>{lastPm?.range?.alarmMin ?? ''}</td>
              <td>{lastPm?.range?.labMin ?? ''}</td>
              <td>{lastPm?.range?.optimalMin ?? ''}</td>
              <td>{lastPm?.range?.optimalMax ?? ''}</td>
              <td>{lastPm?.range?.labMax ?? ''}</td>
              <td>{lastPm?.range?.alarmMax ?? ''}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}


  onHidePdfModal() {
    this.props.onHidePdfModal();
    this.setState({
      showPdfModal: false,
      numResultsToCompare: NUM_RESULTS_TO_COMPARE,
    });
  }

  render() {
    const { bloodworkRanges, currentUserProfile } = this.props;
    const {
      showUpgradeModal,
      showPdfModal,
      numResultsToCompare,
      headerLogoSrc,
    } = this.state;
   
    let { bloodworks } = this.props;
    if (!bloodworks || bloodworks.length === 0) {
      return null;
    }

    if (!currentUserProfile.showClientComparisonTable) {
      return null;
    }

    bloodworks = bloodworks.slice(0, numResultsToCompare);

    const bloodworkAnalysisHistory: BloodworkAnalysis[] = bloodworks
      .reverse()
      .map(bloodwork => {
        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init();
        bloodworkAnalysis.populatePanelMarkersArray();
        return bloodworkAnalysis;
      });

    if (bloodworkAnalysisHistory.length >= 2) {
      bloodworkAnalysisHistory[
        bloodworkAnalysisHistory.length - 1
      ].preparePreviousPanelMarkers(
        bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 2],
      );
    }
    const today = new Date().toISOString();
    const mostRecentBloodwork = bloodworks[bloodworks.length - 1];

    // Fallback logo if preview URL hasn’t resolved yet
    const fallbackLogo =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    const logoSrc = headerLogoSrc || fallbackLogo;
    console.log(
      '[BloodworkComparison] Render - headerLogoSrc:',
      headerLogoSrc ? `${headerLogoSrc.substring(0, 50)}...` : 'null',
    );

    // Check if the whiteBloodCells marker has any value across the history
    const whiteBloodCellsField = PanelField.ALL_FIELDS.find(
      f => f.id === 'whiteBloodCells',
    );
    const whiteBloodCellsHasValues = whiteBloodCellsField
      ? this.fieldHasAnyValue(whiteBloodCellsField, bloodworkAnalysisHistory)
      : false;

    // ---- Updated packing logic for Nutrients / Electrolytes / Lipids ----
    const hideBlanksPdf = !this.state.showHiddenRows;

    const nutrientsFields    = [...PanelField.NUTRIENTS_FIELDS, ...PanelField.ZINC_FIELDS];
    const electrolytesFields = [...PanelField.ELECTROLYTES_FIELDS];
    const lipidsFields       = [...PanelField.LIPIDS_INFLAM_FIELDS];
    const nutrientsCount     = this.countVisibleFields(nutrientsFields, bloodworkAnalysisHistory);
    const lipidsCount        = this.countVisibleFields(lipidsFields, bloodworkAnalysisHistory);

    /**
     * Packing rules:
     * 1 Nutrients ≤ 5  and Lipids ≤ 6 → Nutrients, Electrolytes, and Lipids all on the same page.
     * 2 Nutrients 6–10               → Nutrients + Electrolytes on same page; Lipids start new page.
     * 3 Nutrients > 10               → Electrolytes + Lipids together on next page.
     */

    const packElectrolytesWithNutrients =
      hideBlanksPdf && nutrientsCount > 0 && nutrientsCount <= 10;

    const packLipidsWithNutElec =
      hideBlanksPdf && nutrientsCount > 0 && nutrientsCount <= 5 && lipidsCount > 0 && lipidsCount <= 6;



    const packLipidsWithElectrolytes =
      hideBlanksPdf && nutrientsCount > 10 && lipidsCount > 0;



    return (
      <>
        <PDFModal
          show={showPdfModal}
          title="Blood Work Comparison"
          client={mostRecentBloodwork.client}
          collectionDate={mostRecentBloodwork.collectionDate}
          elementId="pdf-comparison-results"
          onHide={() => this.onHidePdfModal()}
          preloadedLogoDataUrl={headerLogoSrc}
        >
          <div
            id="pdf-comparison-results"
            style={{ paddingBottom: 10, transform: 'none' }}
          >
          <style>
            {`
              /* Page-break divs: force a break but take up no vertical space */
              #pdf-comparison-results .html2pdf__page-break {
                height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                border: 0 !important;
                page-break-after: always !important;
              }

              /* Kill top margins for headings and tables inside the PDF root */
              #pdf-comparison-results h3,
              #pdf-comparison-results h5 {
                margin-top: 0 !important;
              }

              #pdf-comparison-results table.labsmartz-table {
                margin-top: 0 !important;
              }
            `}
          </style>
            <br />
            <br />
            <br />
            <div className="text-center">
              <Image
                id="logo"
                src={this.getLogoUrl()}
                alt="Current Logo"
                fluid
                style={{
                  maxHeight: '100px',
                  maxWidth: '400px',
                  objectFit: 'contain',
                }}
                // onError={e => {
                //   const img = e.target as HTMLImageElement;
                //   if (img.src !== fallbackLogo) {
                //     img.src = fallbackLogo;
                //   }
                // }}
              />
            </div>
            <div>
              <br />
              <br />
              <h3 className="text-center">
                <strong>Blood Work Comparison Report</strong>
              </h3>
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            {/* html2pdf__page-break will add a page break after the html element */}
            <div>
              <h5>
                <strong>Prepared for</strong>
              </h5>
              <h5>
                {mostRecentBloodwork.client.firstName}{' '}
                {mostRecentBloodwork.client.lastName}
              </h5>
              <br />
              <h5>
                <strong>Most Recent Collection Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">
                  {mostRecentBloodwork.collectionDate}
                </Moment>
              </h5>
              <br />
              <h5>
                <strong>Analysis Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">{today}</Moment>
              </h5>
              <br />
            </div>

          <div className="html2pdf__page-break" style={{ height: 0, margin: 0, padding: 0, border: 0 }}/>

          {/* RBC */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.RBC_FIELDS]);
            return table ? (
              <>
                <div style={{ height: '8px' }} />
                <h5><strong>Red Blood Cell (RBC) Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Iron + Copper */}
          {(() => {
            const hideBlanks = !this.state.showHiddenRows;

            if (hideBlanks && !this.anyIronCoreMarkerHasValue(bloodworkAnalysisHistory)) {
              return null;
            }

            const table = this.comparisonTable(bloodworkAnalysisHistory, [
              ...PanelField.IRON_FIELDS,
              ...PanelField.COPPER_FIELDS,
            ]);
            return table ? (
              <>
                <div style={{ height: '8px' }} />
                <h5><strong>Iron Related Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Immune + WBC Diff */}
          {(() => {
            // When "Hide blank rows" is checked, hide the entire immune section if whiteBloodCells has no values
            if (!whiteBloodCellsHasValues && hideBlanksPdf) return null;

            const table = this.comparisonTable(bloodworkAnalysisHistory, [
              ...PanelField.IMMUNE_SYSTEM_FIELDS,
              ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
            ]);
            return table ? (
              <>
                <div className="html2pdf__page-break" style={{ height: 0, margin: 0, padding: 0, border: 0 }}/>
                <div style={{ height: '8px' }} />
                <h5><strong>Immune System Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Coagulation */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.PLATELETS_FIELDS]);
            return table ? (
              <>
                <div style={{ height: '8px' }} />
                <h5><strong>Coagulation Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Glucose */}
          {(() => {
            const glucoseTable = this.comparisonTable(bloodworkAnalysisHistory, [
              ...PanelField.GLUCOSE_FIELDS,
            ]);

            return glucoseTable ? (
              <>
                {/* Always start Glucose on a new page */}
                <div className="html2pdf__page-break" />
                <div style={{ height: '8px' }} />
                <h5><strong>Glucose Markers</strong></h5>
                <div>{glucoseTable}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}  

          {/* Kidney */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.KIDNEY_FIELDS]);
            return table ? (
              <>
                <div style={{ height: '8px' }} />
                <h5><strong>Kidney Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Protein */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.PROTEINS_FIELDS]);
            return table ? (
              <>
                <div style={{ height: '8px' }} />
                <h5><strong>Protein Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Nutrients + Zinc */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, nutrientsFields);
            return table ? (
              <>
                {/* Always start Nutrients on a new page (unchanged) */}
                <div className="html2pdf__page-break" />
                <div style={{ height: '8px' }} />
                <h5><strong>Nutrient Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            ) : null;
          })()}

          {/* Electrolytes */}
          {(() => {
            const table = this.comparisonTable(bloodworkAnalysisHistory, electrolytesFields);
            if (!table) return null;

            // Only insert the page break if we are NOT packing Electrolytes with Nutrients
            const needBreakBeforeElectrolytes = !packElectrolytesWithNutrients;

            return (
              <>
                {needBreakBeforeElectrolytes && (
                  <div className="html2pdf__page-break" />
                )}
                <div style={{ height: '8px' }} />
                <h5><strong>Electrolyte Markers</strong></h5>
                <div>{table}</div>
                <div style={{ height: '8px' }} />
              </>
            );
          })()}


          {/* ---- PDF section CSS (once for this group) ---- */}
          <style>
            {`
              /* Keep entire section blocks intact across page boundaries */
              .pdf-section,
              .pdf-section .section-block {
                break-inside: avoid;
                page-break-inside: avoid;
              }
              .pdf-section h5 {
                break-after: avoid;
                page-break-after: avoid;
                margin-bottom: 6px;
              }
            `}
          </style>

          {/* ---- Lipids / Liver / Thyroid packing ---- */}
          {(() => {
            const lipidsTable   = this.comparisonTable(bloodworkAnalysisHistory, lipidsFields);
            const liverTable    = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.LIVER_FIELDS]);
            const thyroidTable  = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.THYROID_FIELDS]);

            const showingBlanks = this.state.showHiddenRows; // true = showing blanks in UI/PDF
            const hideBlanks    = !showingBlanks;            // true = rows hidden (apply new rules)

            const Section = ({
              title,
              table,
              className = 'pdf-section',
            }: {
              title: string;
              table: React.ReactNode | null;
              className?: string;
            }) => (table ? (
              <div className={className}>
                <div style={{ height: '8px' }} />
                <h5><strong>{title}</strong></h5>
                <div className="section-block">{table}</div>
                <div style={{ height: '8px' }} />
              </div>
            ) : null);

            if (lipidsTable) {
              // When showing blanks, keep Electrolytes + Lipids on the same page.
              // When hiding blanks, insert a break before Lipids unless we're packing with Nutrients/Electrolytes.
              const needBreakBeforeLipids = showingBlanks
                ? false
                : !(packLipidsWithNutElec || packLipidsWithElectrolytes);

              return (
                <>
                  {needBreakBeforeLipids && (
                    <div className="html2pdf__page-break" />
                  )}

                  <Section title="Lipids / Inflammation Markers" table={lipidsTable} />

                  {hideBlanks && needBreakBeforeLipids ? (
                    // Rows hidden AND Lipids were moved to a new page:
                    // Put LIVER on the SAME page as Lipids, then THYROID on the NEXT page.
                    <>
                      <Section title="Liver / Biliary Markers" table={liverTable} />
                      {thyroidTable ? (
                        <>
                          <div className="html2pdf__page-break" />
                          <Section title="Thyroid Markers" table={thyroidTable} />
                        </>
                      ) : null}
                    </>
                  ) : (
                    // Default (incl. showing blanks): start a fresh page for Liver + Thyroid together
                    <div className="pdf-section starts-on-new-page">
                      <Section title="Liver / Biliary Markers" table={liverTable} />
                      <Section title="Thyroid Markers" table={thyroidTable} />
                    </div>
                  )}
                </>
              );
            }

            // No Lipids
            if (hideBlanks) {
              // Rows hidden & NO Lipids: Liver and Thyroid on the SAME page.
              return (
                <>
                  <Section title="Liver / Biliary Markers" table={liverTable} />
                  <Section title="Thyroid Markers" table={thyroidTable} />
                </>
              );
            }

            // Showing blanks & NO Lipids: keep your previous behavior (Thyroid starts on next page if present)
            return (
              <>
                <Section title="Liver / Biliary Markers" table={liverTable} />
                {thyroidTable ? (
                  <>
                    <div className="html2pdf__page-break" />
                    <Section title="Thyroid Markers" table={thyroidTable} />
                  </>
                ) : null}
              </>
            );
          })()}


          {/* Hormones */}
          {(() => {
            const liverTable = this.comparisonTable(bloodworkAnalysisHistory, [
              ...PanelField.LIVER_FIELDS,
            ]);
            const thyroidTable = this.comparisonTable(bloodworkAnalysisHistory, [
              ...PanelField.THYROID_FIELDS,
            ]);

          // Base hormone fields (no menstrual phase here)
          const hormoneFieldsBase = [
            ...PanelField.HORMONE_FIELDS_1,
            ...PanelField.HORMONE_FIELDS_2,
          ].filter(f => f.id !== 'menstrualPhase');

          const hideBlanks = !this.state.showHiddenRows;

                // When "Hide blank rows" is checked, hide section if no markers have any values
                  const anyHormoneHasValue = hormoneFieldsBase.some(f =>
                    this.fieldHasAnyValue(f, bloodworkAnalysisHistory),
                    );
                    if (!anyHormoneHasValue && hideBlanks) return null;

                // When at least one hormone has value, include menstrual phase and
                // pass the *full* base list so the global "Hide blank rows" works normally
                const menstrualPhaseField = PanelField.ALL_FIELDS.find(f => f.id === 'menstrualPhase');
                const hormoneFieldsForTable = [
                  ...(menstrualPhaseField ? [menstrualPhaseField] : []),
                  ...hormoneFieldsBase,
                ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

                const hormoneTable = this.comparisonTable(
                  bloodworkAnalysisHistory,
                  hormoneFieldsForTable,
                  mostRecentBloodwork.client.gender, 
                );

                return hormoneTable ? (
                  <>
                    {(liverTable || thyroidTable) && (
                      <div className="html2pdf__page-break" />
                    )}
                    <div style={{ height: '8px' }} />
                    <h5><strong>Hormone Markers</strong></h5>
                    <div
                      className="hormone-pdf-section"
                      style={{
                        marginBottom: 0,
                        paddingBottom: 10,
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                      }}
                    >
                      <style>
                        {`
                          .hormone-pdf-section td,
                          .hormone-pdf-section th {
                            padding-top: 3px !important;
                            padding-bottom: 3px !important;
                          }
                        `}
                      </style>
                      {hormoneTable}
                    </div>
                  </>
                ) : null;
              })()}


    </div>
      </PDFModal>

        {showPdfModal ? null : (
          <>
            <Card>
              <Card.Header>
                Blood Work Comparison&nbsp;
                <span className="float-right d-flex align-items-center">
                  <label
                    htmlFor="hideEmptyRowsToggle"
                    style={{
                      color: 'white',
                      marginRight: '8px',
                      marginBottom: '0',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ marginRight: '6px' }}>Hide blank rows</span>
                      <input
                        id="hideEmptyRowsToggle"
                        type="checkbox"
                        checked={!this.state.showHiddenRows}
                        onChange={(e) =>
                          this.setState({ showHiddenRows: !e.target.checked })
                        }
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          backgroundColor: 'white', // white background box
                          border: '1px solid white', // blends with header
                          borderRadius: '2px',
                          position: 'relative',
                          marginRight: '12px',
                        }}
                      />
                  </label>               
                  <OverlayTrigger
                    overlay={this.exportInfoTooltip()}
                    placement="bottom"
                  >
                    <FontAwesomeIcon
                      icon="info-circle"
                      onClick={this.openExportInfoWindow}
                    />
                  </OverlayTrigger>
                  &nbsp;&nbsp;&nbsp;
                  <OverlayTrigger
                    overlay={this.exportTooltip()}
                    placement="bottom"
                  >
                    <FontAwesomeIcon
                      icon={faFileDownload}
                      onClick={() => this.export()}
                    />
                  </OverlayTrigger>
                </span>
              </Card.Header>
              <Card.Body>
              {/* RBC */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.RBC_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Red Blood Cell (RBC) Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Iron + Copper */}
              {(() => {
                const hideBlanks = !this.state.showHiddenRows;

                if (hideBlanks && !this.anyIronCoreMarkerHasValue(bloodworkAnalysisHistory)) {
                  return null;
                }

                const table = this.comparisonTable(bloodworkAnalysisHistory, [
                  ...PanelField.IRON_FIELDS,
                  ...PanelField.COPPER_FIELDS,
                ]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Iron Related Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Immune + WBC Diff */}
              {(() => {
                const hideBlanks = !this.state.showHiddenRows;

                // When "Hide blank rows" is checked, hide the entire immune section if whiteBloodCells has no values
                if (!whiteBloodCellsHasValues && hideBlanks) return null;

                const table = this.comparisonTable(bloodworkAnalysisHistory, [
                  ...PanelField.IMMUNE_SYSTEM_FIELDS,
                  ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
                ]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Immune System Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Coagulation */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.PLATELETS_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Coagulation Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Glucose */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.GLUCOSE_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Glucose Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Kidney */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.KIDNEY_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Kidney Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Protein */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.PROTEINS_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Protein Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Nutrients + Zinc */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [
                  ...PanelField.NUTRIENTS_FIELDS,
                  ...PanelField.ZINC_FIELDS,
                ]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Nutrient Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Electrolytes */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.ELECTROLYTES_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Electrolyte Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Lipids / Inflammation */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.LIPIDS_INFLAM_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Lipids / Inflammation Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Liver / Biliary */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory, [...PanelField.LIVER_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Liver / Biliary Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Thyroid */}
              {(() => {
                const table = this.comparisonTable(bloodworkAnalysisHistory,
                  [...PanelField.THYROID_FIELDS]);
                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Thyroid Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}

              {/* Hormones */}
              {(() => {
                // Base hormone fields, explicitly excluding menstrual phase
                  const hormoneFieldsBase = [
                    ...PanelField.HORMONE_FIELDS_1,
                    ...PanelField.HORMONE_FIELDS_2,
                  ].filter(f => f.id !== 'menstrualPhase');

                 const hideBlanks = !this.state.showHiddenRows;

                // When "Hide blank rows" is checked, hide section if no markers have any values
                  const anyHormoneHasValue = hormoneFieldsBase.some(f =>
                    this.fieldHasAnyValue(f, bloodworkAnalysisHistory),
                    );
                    if (!anyHormoneHasValue && hideBlanks) return null;

                // Include menstrual phase and pass the *full* base list
                const menstrualPhaseField = PanelField.ALL_FIELDS.find(f => f.id === 'menstrualPhase');
                const hormoneFieldsForTable = [
                  ...(menstrualPhaseField ? [menstrualPhaseField] : []),
                  ...hormoneFieldsBase,
                ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

                const table = this.comparisonTable(
                  bloodworkAnalysisHistory,
                  hormoneFieldsForTable,
                  mostRecentBloodwork.client.gender,
                );

                return table ? (
                  <>
                    <div style={{ height: '8px' }} />
                    <h5><strong>Hormone Markers</strong></h5>
                    {table}
                    <div style={{ height: '8px' }} />
                  </>
                ) : null;
              })()}


              </Card.Body>
            </Card>

            <GenericModal
              show={showUpgradeModal}
              title="Export to PDF Feature Not Available"
              text="Export to PDF feature is not available with a free account."
              link="https://labsmarts.com/pricing"
              linkText="Click here to upgrade."
              onHide={() => this.setState({ showUpgradeModal: false })}
            />
          </>
        )}
      </>
    );
  }
}
